import {
  CognitoIdentityProviderClient,
  InitiateAuthCommand,
  SignUpCommand,
  ConfirmSignUpCommand,
  ForgotPasswordCommand,
  ConfirmForgotPasswordCommand,
  ChangePasswordCommand,
} from '@aws-sdk/client-cognito-identity-provider';
import { LoginCredentials, AuthResponse } from '../types';

const COGNITO_REGION = process.env.REACT_APP_COGNITO_REGION || 'ap-south-1';
const COGNITO_CLIENT_ID = process.env.REACT_APP_COGNITO_CLIENT_ID || '697c5cgm91q0c38eejqkd2dhmr';

const cognitoClient = new CognitoIdentityProviderClient({
  region: COGNITO_REGION,
});

export const authService = {
  /**
   * Login with username and password
   */
  async login(credentials: LoginCredentials): Promise<AuthResponse> {
    const command = new InitiateAuthCommand({
      AuthFlow: 'USER_PASSWORD_AUTH',
      ClientId: COGNITO_CLIENT_ID,
      AuthParameters: {
        USERNAME: credentials.username,
        PASSWORD: credentials.password,
      },
    });

    const response = await cognitoClient.send(command);
    
    if (!response.AuthenticationResult) {
      throw new Error('Authentication failed');
    }

    return {
      IdToken: response.AuthenticationResult.IdToken!,
      AccessToken: response.AuthenticationResult.AccessToken!,
      RefreshToken: response.AuthenticationResult.RefreshToken!,
    };
  },

  /**
   * Sign up a new user
   */
  async signUp(username: string, password: string, email: string): Promise<void> {
    const command = new SignUpCommand({
      ClientId: COGNITO_CLIENT_ID,
      Username: username,
      Password: password,
      UserAttributes: [
        { Name: 'email', Value: email },
      ],
    });

    await cognitoClient.send(command);
  },

  /**
   * Confirm sign up with verification code
   */
  async confirmSignUp(username: string, code: string): Promise<void> {
    const command = new ConfirmSignUpCommand({
      ClientId: COGNITO_CLIENT_ID,
      Username: username,
      ConfirmationCode: code,
    });

    await cognitoClient.send(command);
  },

  /**
   * Initiate forgot password flow - sends verification code to user's email
   */
  async forgotPassword(username: string): Promise<void> {
    const command = new ForgotPasswordCommand({
      ClientId: COGNITO_CLIENT_ID,
      Username: username,
    });

    await cognitoClient.send(command);
  },

  /**
   * Confirm forgot password with verification code and new password
   */
  async confirmForgotPassword(username: string, code: string, newPassword: string): Promise<void> {
    const command = new ConfirmForgotPasswordCommand({
      ClientId: COGNITO_CLIENT_ID,
      Username: username,
      ConfirmationCode: code,
      Password: newPassword,
    });

    await cognitoClient.send(command);
  },

  /**
   * Change password for authenticated user
   */
  async changePassword(accessToken: string, previousPassword: string, proposedPassword: string): Promise<void> {
    const command = new ChangePasswordCommand({
      AccessToken: accessToken,
      PreviousPassword: previousPassword,
      ProposedPassword: proposedPassword,
    });

    await cognitoClient.send(command);
  },

  /**
   * Parse JWT token to extract user info
   */
  parseToken(token: string): { userId: string; username: string; exp: number } {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(window.atob(base64));
    
    return {
      userId: payload.sub,
      username: payload['cognito:username'],
      exp: payload.exp,
    };
  },

  /**
   * Check if token is expired
   */
  isTokenExpired(token: string): boolean {
    try {
      const { exp } = this.parseToken(token);
      return Date.now() >= exp * 1000;
    } catch {
      return true;
    }
  },
};
